import Box from '@mui/material/Box'
import { lightBlue } from '@mui/material/colors'
import { alpha } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'

const DEFAULT_THUMBNAILS = Object.freeze({
  dark: 'https://a.basemaps.cartocdn.com/dark_all/12/772/1644.png',
  roadmap: 'https://a.basemaps.cartocdn.com/light_all/12/772/1644.png',
  satellite: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/12/1644/772',
})

const SVG_OVERLAY_SX = { position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }
const getLabelSx = theme => ({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  px: '4px',
  py: '2px',
  bgcolor: alpha(theme.palette.common.black, 0.62),
  color: theme.palette.common.white,
  // Sub-overline size: 56 px thumbnail needs a smaller caption than the scale offers.
  fontSize: '0.625rem',
  fontWeight: 700,
  letterSpacing: '0.3px',
  textAlign: 'center',
  textTransform: 'uppercase',
  pointerEvents: 'none',
})

const MapStyleThumbnail = ({ isSatellite, onToggle, baseStyle = 'dark', thumbnails = DEFAULT_THUMBNAILS }) => {
  // Light-theme maps (the bulk dialogs) ride a roadmap basemap, not the master
  // board's dark one — preview that instead so the thumbnail matches reality.
  const previewStyle = isSatellite ? baseStyle : 'satellite'
  const previewLabel = isSatellite ? 'Map' : 'Sat'
  const buttonSx = useMemo(
    () => theme => {
      const isDark = theme.palette.mode === 'dark'
      const accent = isDark ? lightBlue[400] : theme.palette.primary.main
      const { glass } = theme.palette
      return {
        position: 'relative',
        width: 56,
        height: 56,
        padding: 0,
        border: `2px solid ${glass?.border ?? alpha(theme.palette.divider, 0.6)}`,
        borderRadius: '4px',
        overflow: 'hidden',
        cursor: 'pointer',
        backgroundImage: `url("${thumbnails[previewStyle]}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: glass?.surfaceDeep ?? theme.palette.background.paper,
        boxShadow: glass?.shadow ?? theme.shadows[2],
        transition: 'border-color 120ms ease, transform 120ms ease',
        '&:hover': { borderColor: accent, transform: 'scale(1.04)' },
      }
    },
    [thumbnails, previewStyle]
  )
  return (
    <Tooltip arrow placement="bottom" title={isSatellite ? 'Switch to dark map' : 'Switch to satellite imagery'}>
      <Box
        aria-label={`Switch basemap to ${previewLabel.toLowerCase()}`}
        aria-pressed={isSatellite}
        component="button"
        sx={buttonSx}
        type="button"
        onClick={onToggle}
      >
        {previewStyle === 'dark' && (
          <Box component="svg" sx={SVG_OVERLAY_SX} viewBox="0 0 56 56">
            <line opacity="0.3" stroke={lightBlue[300]} strokeWidth="1.5" x1="0" x2="56" y1="22" y2="18" />
            <line opacity="0.3" stroke={lightBlue[300]} strokeWidth="1.5" x1="0" x2="56" y1="44" y2="48" />
            <line opacity="0.3" stroke={lightBlue[300]} strokeWidth="1.5" x1="16" x2="24" y1="0" y2="56" />
            <path
              d="M 4 48 L 18 38 L 30 40 L 42 22 L 52 14"
              fill="none"
              opacity="0.95"
              stroke={lightBlue[300]}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="3"
            />
            <circle cx="4" cy="48" fill={lightBlue[300]} r="2.5" />
            <circle cx="30" cy="40" fill={lightBlue[300]} r="2.5" />
            <circle cx="52" cy="14" fill={lightBlue[300]} r="2.5" />
          </Box>
        )}
        <Box sx={getLabelSx}>{previewLabel}</Box>
      </Box>
    </Tooltip>
  )
}

MapStyleThumbnail.propTypes = {
  isSatellite: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  baseStyle: PropTypes.oneOf(['dark', 'roadmap']),
  thumbnails: PropTypes.shape({
    dark: PropTypes.string,
    roadmap: PropTypes.string,
    satellite: PropTypes.string.isRequired,
  }),
}

export default MapStyleThumbnail
